// `runMutationWithToast` — thin wrapper around the `try { await fn();
// toast.success } catch { toast.error }` shape that recurred across ~10
// async mutation handlers (businesses, voice, onboarding, leads export).
//
// It is a plain async helper rather than a React hook on purpose: it needs
// no hook state. `toast` is a module singleton and the i18n-resolved titles
// are passed in by the caller, so there is nothing to memoise.
//
// Semantics are deliberately identical to the inlined try/catch it replaces:
//   - runs `run()`;
//   - on success, shows the success toast (if provided), then awaits
//     `onSuccess(value)` — success-only side effects, in the same order as
//     the original (toast first, then side effects);
//   - on error, shows the error toast (if provided), then runs
//     `onError(err)`; the error is swallowed (never rethrown), matching the
//     bare `catch {}` blocks at every call site.
//
// `success`/`error` accept either static `ToastOptions` or a function of the
// awaited value / caught error, so callers that interpolate the result into
// the toast title (e.g. "{count} applied") keep byte-identical output.

import { toast, type ToastOptions } from '@barel/ui';

export interface MutationToastConfig<T> {
  /** Success toast — omit to show none (some handlers toast errors only). */
  success?: ToastOptions | ((value: T) => ToastOptions);
  /** Error toast — omit to show none. */
  error?: ToastOptions | ((err: unknown) => ToastOptions);
  /** Success-only side effects, run after the success toast. */
  onSuccess?: (value: T) => void | Promise<void>;
  /** Error-only side effects, run after the error toast. */
  onError?: (err: unknown) => void;
}

export async function runMutationWithToast<T>(
  run: () => Promise<T>,
  config: MutationToastConfig<T>,
): Promise<void> {
  try {
    const value = await run();
    if (config.success) {
      toast.success(typeof config.success === 'function' ? config.success(value) : config.success);
    }
    await config.onSuccess?.(value);
  } catch (err) {
    if (config.error) {
      toast.error(typeof config.error === 'function' ? config.error(err) : config.error);
    }
    config.onError?.(err);
  }
}
